import { useCallback, useEffect } from 'react';

import type { FormUsageContext } from '#/components/Form/Form';
import { Form } from '#/components/Form/Form';
import type {
  FormContextValue,
  FormSubmitFunction,
} from '#/components/Form/FormContext';
import { BodySmall } from '#/components/Text/BodySmall';
import { H1 } from '#/components/Text/H1';
import { useI18n } from '#/i18n/hooks/useI18n';
import { salaryApiFetchCareerPathSynonyms } from '#/modules/salary/api/salaryApiFetchCareerPathSynonyms';
import { salaryApiFetchReport } from '#/modules/salary/api/salaryApiFetchReport';
import { MainChartSidebarForm } from '#/modules/salary/components/MainChartSidebarForm/MainChartSidebarForm';
import { salariesFaqRoute } from '#/modules/salary/routing/salary.routes';
import type { CalculatorReport } from '#/modules/salary/types/CalculatorReport';
import type { EstimatedRange } from '#/modules/salary/types/EstimatedRange';
import type { SalariesReportFormData } from '#/modules/salary/types/SalariesReportFormData';
import { colors } from '#/theme/theme';
import { trackEvent } from '#/utils/analytics/track/trackEvent';

import {
  MainChartSidebarContainer,
  MainChartSidebarFormContainer,
  MainChartSidebarHeadingContainer,
} from './MainChartSidebar.styled';

type MainChartSidebarProps = {
  formAnalyticsTitle: string;
  formUsageContext: FormUsageContext;
  setInitialized: (initialized: boolean) => void;
  setLoading: (loading: boolean) => void;
  setCareerPathData: (synonyms: EstimatedRange[]) => void;
  setResults: (
    calculatorResult: CalculatorReport,
    values: SalariesReportFormData,
  ) => void;
  searchApiKey: string;
  setSearchApiKey: (arg0: string) => void;
  initialSurveyReportFormData: SalariesReportFormData | null;
};

export function MainChartSidebar({
  formAnalyticsTitle,
  formUsageContext,
  setLoading,
  setInitialized,
  setResults,
  setCareerPathData,
  searchApiKey,
  setSearchApiKey,
  initialSurveyReportFormData,
}: MainChartSidebarProps) {
  const { i18n } = useI18n();

  const getReport = useCallback(
    async (
      values: SalariesReportFormData,
      updateSubmitting?: FormContextValue<SalariesReportFormData>['updateSubmitting'],
    ) => {
      setLoading(true);

      const calculatorResult = await salaryApiFetchReport(values);

      const careerPath = await salaryApiFetchCareerPathSynonyms({
        ...values,
      });

      setResults(calculatorResult, values);
      setCareerPathData(careerPath.filter((career) => career.salary !== null));
      setLoading(false);

      if (updateSubmitting) {
        updateSubmitting(false);
      }
      setInitialized(true);
    },
    [setLoading, setCareerPathData, setInitialized, setResults],
  );

  const onSubmit: FormSubmitFunction<SalariesReportFormData> = ({
    values,
    handleError,
    updateSubmitting,
  }) =>
    getReport(values, updateSubmitting).catch((e) => {
      updateSubmitting(false);
      setLoading(false);
      trackEvent('Error: Calculator Search');
      handleError(e as Error);
    });

  useEffect(() => {
    if (initialSurveyReportFormData) {
      void getReport(initialSurveyReportFormData);
    }
  }, [initialSurveyReportFormData, getReport]);

  return (
    <MainChartSidebarContainer>
      <MainChartSidebarHeadingContainer>
        <H1 color={colors.white} styleAs="h2">
          {i18n('Explore Salaries')}
        </H1>
      </MainChartSidebarHeadingContainer>

      <BodySmall
        lineHeight={1.625}
        color={colors.white}
        textAlign={['center', null, null, 'left']}
      >
        {i18n('Learn what you could be earning in the nonprofit sector.')}{' '}
        <a
          href={salariesFaqRoute.fullPath}
          target="_blank"
          style={{ color: colors.white }}
          rel="noreferrer"
        >
          {i18n('See our FAQ for further information.')}
        </a>
      </BodySmall>

      <MainChartSidebarFormContainer>
        <Form<SalariesReportFormData>
          analyticsTitle={formAnalyticsTitle}
          usageContext={formUsageContext}
          qaId="main-chart-form"
          initialValues={initialSurveyReportFormData ?? {}}
          onSubmit={onSubmit}
          render={() => (
            <MainChartSidebarForm
              searchApiKey={searchApiKey}
              setSearchApiKey={setSearchApiKey}
            />
          )}
        />
      </MainChartSidebarFormContainer>
    </MainChartSidebarContainer>
  );
}
